/// <reference types="google.maps" />
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { Logger } from '@sondermind/datadog-logging';

import { GOOGLE_MAPS_API_KEY } from '../shared/constants';

const API_ENDPOINT = 'https://maps.googleapis.com/maps/api/geocode/json';
const TIMEZONE_API_ENDPOINT = 'https://maps.googleapis.com/maps/api/timezone/json';
const MILLIS_PER_SECOND = 1000;

interface IGeocodeJsonResponse {
  status: string;
  results?: google.maps.GeocoderResult[];
}

interface ITimeZoneJsonResponse {
  status: string;
  timeZoneId?: string;
  timeZoneName?: string;
  errorMessage?: string;
}

export interface ICityLatLng {
  lat: number;
  lng: number;
}

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsGeocodeService {
  private readonly cityStateCache = new Map<string, ICityLatLng | null>();
  private readonly cityStateInFlight = new Map<string, Observable<ICityLatLng | null>>();
  private readonly logger = inject(Logger, { optional: true });

  constructor(private httpClient: HttpClient) {}

  /**
   * Resolve the IANA time zone id (e.g. `America/New_York`) for a coordinate via the Google
   * Time Zone API. Returns `null` if the lookup fails or the API returns a non-OK status, so
   * callers can fall back to the existing zone rather than persisting a bad value.
   *
   * @param lat latitude of the location
   * @param lng longitude of the location
   */
  getTimeZoneId$(lat: number, lng: number): Observable<string | null> {
    const timestampSeconds = Math.floor(Date.now() / MILLIS_PER_SECOND);
    const url = `${TIMEZONE_API_ENDPOINT}?location=${lat},${lng}`
      + `&timestamp=${timestampSeconds}`
      + `&key=${GOOGLE_MAPS_API_KEY}`;

    return this.httpClient.get<ITimeZoneJsonResponse>(url).pipe(
      map((response) => {
        if (response.status !== 'OK' || !response.timeZoneId) {
          this.logger?.warn('GoogleMapsGeocodeService: time zone lookup returned no zone', {
            timeZoneStatus: response.status,
            errorMessage: response.errorMessage,
            lat,
            lng,
          });
          return null;
        }
        return response.timeZoneId;
      }),
      catchError((error: unknown) => {
        const errorDetail = error instanceof Error ? error.message : String(error);
        this.logger?.error('GoogleMapsGeocodeService: time zone lookup request failed', {
          lat,
          lng,
          error: errorDetail,
        });
        return of(null);
      })
    );
  }

  reverseGeocode(lat: number, lng: number): Observable<google.maps.GeocoderResult[]> {
    const url = `${API_ENDPOINT}?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`;
    return this.httpClient.get<google.maps.GeocoderResponse>(url).pipe(
      map((response: google.maps.GeocoderResponse) => response.results)
    );
  }

  geocodeCityState$(city: string, state: string): Observable<ICityLatLng | null> {
    const c = city?.trim() ?? '';
    const s = state?.trim() ?? '';
    if (!c || !s) {
      return of(null);
    }

    const key = this.cityStateCacheKey(c, s);
    const cached = this.cityStateCache.get(key);
    if (cached !== undefined) {
      return of(cached);
    }

    const existing = this.cityStateInFlight.get(key);
    if (existing) {
      return existing;
    }

    const request$ = this.createGeocodeRequest$(c, s).pipe(
      tap((result) => {
        this.cityStateCache.set(key, result);
        this.cityStateInFlight.delete(key);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.cityStateInFlight.set(key, request$);
    return request$;
  }

  private cityStateCacheKey(city: string, state: string): string {
    return `${city.trim().toLowerCase()}|${state.trim().toLowerCase()}`;
  }

  private createGeocodeRequest$(city: string, state: string): Observable<ICityLatLng | null> {
    const address = `${city}, ${state}, USA`;
    const url = `${API_ENDPOINT}?address=${encodeURIComponent(address)}&region=us&key=${GOOGLE_MAPS_API_KEY}`;

    return this.httpClient.get<IGeocodeJsonResponse>(url).pipe(
      map((response) => {
        const result = this.extractLatLng(response);
        if (result === null) {
          this.logger?.warn('GoogleMapsGeocodeService: city/state geocode returned no coordinates', {
            geocodeStatus: response.status,
            city,
            state,
            resultCount: response.results?.length ?? 0,
          });
        }
        return result;
      }),
      catchError((error: unknown) => {
        const errorDetail = error instanceof Error ? error.message : String(error);
        this.logger?.error('GoogleMapsGeocodeService: city/state geocode request failed', {
          city,
          state,
          error: errorDetail,
        });
        return of(null);
      })
    );
  }

  private extractLatLng(response: IGeocodeJsonResponse): ICityLatLng | null {
    if (response.status !== 'OK' || !response.results?.length) {
      return null;
    }

    // Geocode JSON returns lat/lng numbers; @types/google.maps types geometry.location as LatLng (methods).
    const location = response.results[0]?.geometry?.location as unknown;
    if (
      location == null ||
      typeof location !== 'object' ||
      !('lat' in location) ||
      !('lng' in location)
    ) {
      return null;
    }
    const loc = location as { lat: unknown; lng: unknown; };
    if (typeof loc.lat !== 'number' || typeof loc.lng !== 'number') {
      return null;
    }

    return { lat: loc.lat, lng: loc.lng };
  }
}
